import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Inscription, useResponsive } from '@common-fe/common-fe';
import Lottie from 'lottie-react';

import AllSetIcon from '@/assets/bank-icons/all-set.json';
import routes from '@/common/routes';
import { ModalWrapper } from '@/components/wrappers';
import { CardActivationVariants } from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/VerifyMyCardModal.constants';
import { useHistory } from '@/modules/core/hooks';
import { getTokenData } from '@/modules/user/hooks/useAuth.ts';
import { useAuthStore } from '@/modules/user/stores';
import theme from '@/styles/theme';

const BUTTON_WIDTH = '200px';

interface Props {
  onSetVisible: (value: boolean) => void;
  isEmailMethod?: boolean;
  refreshCardList?: () => void;
  isActivationSuccess: CardActivationVariants;
}

const SuccessActivatedModal: React.FC<Props> = ({
  isEmailMethod,
  onSetVisible,
  refreshCardList,
  isActivationSuccess,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { push } = useHistory();
  const store = useAuthStore();

  const handleNavigate = useCallback(
    (route: string) => {
      const { role } = getTokenData();
      push(route);

      store.setUser({
        ...store.user,
        role,
      });
      if (refreshCardList) refreshCardList();
      onSetVisible(false);
    },
    [push, store, refreshCardList, onSetVisible]
  );

  const handleSetVisible = useCallback(
    (value: boolean) => {
      if (!value) {
        handleNavigate(routes.HOME);
      }
      if (value) {
        onSetVisible(value);
      }
    },
    [handleNavigate, onSetVisible]
  );

  const EmailMethodChildrenNode = useMemo(() => {
    if (isActivationSuccess === CardActivationVariants.ACTIVATED) {
      return (
        <>
          <Inscription margin={{ top: isMobile ? 'spacing8' : 'spacing12' }} weight={700} textAlign="center">
            {t('This card has been activated!')}
          </Inscription>
          <Inscription
            margin={{
              top: isMobile ? 'spacing8' : 'spacing12',
              horizontal: isMobile ? 'spacing12' : undefined,
            }}
            textAlign="center"
          >
            {t('Did you also receive a card for your dependent?')}
          </Inscription>
          <Inscription textAlign="center" margin={{ horizontal: isMobile ? 'spacing12' : undefined }}>
            {t('You can activate or request a new card in the Cards dashboard.')}
          </Inscription>
        </>
      );
    }

    if (isActivationSuccess === CardActivationVariants.ACTIVATION_NOT_INIT) {
      return (
        <>
          <Inscription margin={{ top: isMobile ? 'spacing8' : 'spacing12' }} weight={700} textAlign="center">
            {t('Your card has been verified but not activated. Don\'t forget to activate your card before using.')}
          </Inscription>
          <Inscription
            margin={{
              top: isMobile ? 'spacing8' : 'spacing12',
              horizontal: isMobile ? 'spacing12' : undefined,
            }}
            textAlign="center"
          >
            {t('Order and activate dependent cards in the Cards Dashboard.')}
          </Inscription>
        </>
      );
    }
  }, [isActivationSuccess, isMobile, t]);

  const childrenNode = useMemo(() => {
    if (isEmailMethod) {
      return EmailMethodChildrenNode;
    }

    return (
      <>
        {isActivationSuccess === CardActivationVariants.ACTIVATED && (
          <Inscription margin={{ top: isMobile ? 'spacing8' : 'spacing12' }} weight={700} textAlign="center">
            {t(
              "Your card has been verified but not activated. Don't forget to activate your card before using."
            )}
          </Inscription>
        )}

        {isActivationSuccess === CardActivationVariants.ERROR_ACTIVATION && (
          <>
            <Inscription
              margin={{
                top: isMobile ? 'spacing8' : 'spacing12',
                horizontal: isMobile ? 'spacing12' : undefined,
              }}
              weight={700}
              textAlign="center"
            >
              {t('We encountered an unexpected error and couldn’t activate the card.')}
            </Inscription>
            <Inscription
              weight={700}
              textAlign="center"
              margin={{ horizontal: isMobile ? 'spacing12' : undefined }}
            >
              {t('Try again on the Cards Dashboard.')}
            </Inscription>
          </>
        )}
        <>
          <Inscription
            margin={{
              top: isMobile ? 'spacing8' : 'spacing12',
              horizontal: isMobile ? 'spacing12' : undefined,
            }}
            textAlign="center"
          >
            {t('Did you also receive a card for your dependent?')}
          </Inscription>
          <Inscription textAlign="center" margin={{ horizontal: isMobile ? 'spacing12' : undefined }}>
            {t('You can activate or request a new card in the Cards dashboard.')}
          </Inscription>
        </>
      </>
    );
  }, [isActivationSuccess, isEmailMethod, isMobile, t, EmailMethodChildrenNode]);

  return (
    <ModalWrapper visible onSetVisible={handleSetVisible} testId="success-activated">
      <Box margin={{ top: isMobile ? 'spacing8' : 'spacing12' }} align="center">
        <Lottie
          animationData={AllSetIcon}
          style={{
            width: isMobile ? '128px' : undefined,
            height: isMobile ? '128px' : undefined,
          }}
        />
        <Inscription
          margin={{
            top: isMobile ? 'spacing16' : 'spacing8',
            horizontal: isMobile ? 'spacing12' : undefined,
          }}
          size={theme.spacings.spacing24}
          weight={700}
          textAlign="center"
        >
          {t("You're all set!")}
        </Inscription>

        {childrenNode}
      </Box>

      <Box
        direction={isMobile ? 'column' : 'row'}
        margin={isMobile ? { top: 'spacing16' } : { vertical: 'spacing24' }}
        justify="center"
        pad={isMobile ? { horizontal: 'spacing4' } : undefined}
      >
        <Box
          direction={isMobile ? 'column-reverse' : 'row-reverse'}
          gap="spacing12"
          align="center"
          justify="center"
          fill={isMobile}
        >
          <AppButton
            fill={isMobile}
            testId="close"
            buttonType="primary"
            onClick={() => handleNavigate(routes.HOME)}
            width={BUTTON_WIDTH}
          >
            {t('Close')}
          </AppButton>
          <AppButton
            fill={isMobile}
            buttonType="secondary"
            testId="go-to-cards-dashboard"
            onClick={() => handleNavigate(routes.CARDS)}
            width={BUTTON_WIDTH}
          >
            {t('Go to Cards Dashboard')}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default SuccessActivatedModal;
